import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { PdfExportedElement } from '@emma-services/export-pdf.service';
import { WidgetConfigService } from '@emma-services/widget-config.service';
import { OnUnsubscribe } from '@platform/helpers/on-unsubscribe.class';
import { getWidgetFilters, getWidgetGroupBys, getWidgetSubgroupBys } from '@emma-helpers/widget.helper';
import { CustomDashboardsInfoChart, WidgetConfig, WidgetGroupBy } from 'emma-common-ts/emma';
import { difference } from 'lodash';
import { BehaviorSubject, of } from 'rxjs';
import { filter as rxFilter, switchMap, takeUntil } from 'rxjs/operators';
import { EMMAChartComponent } from './charts/emma-chart.component';

@Component({
    selector: 'emma-editable-widget',
    templateUrl: './emma-editable-widget.component.html',
    standalone: false
})
export class EMMAEditableWidgetComponent extends OnUnsubscribe implements OnChanges {
  @Input()
  widget!: CustomDashboardsInfoChart;
  @Output()
  remove = new EventEmitter<CustomDashboardsInfoChart>();
  @Output()
  expand = new EventEmitter<CustomDashboardsInfoChart>();
  @Output()
  edit = new EventEmitter<CustomDashboardsInfoChart>();
  @Input()
  editable = false;
  @Input()
  expandable = false;
  @Input()
  removable = false;
  @Input()
  export = false;

  @ViewChild(EMMAChartComponent, { static: false })
  chartView!: EMMAChartComponent;
  chartId = '';
  config?: WidgetConfig;
  altTitle = '';
  layout = '';
  isChartLoaded = false;
  isError = false;
  $isLoaded = new BehaviorSubject<boolean>(false);
  isRemove = false;
  exportEmitter = new EventEmitter<void>();
  @Output()
  exported = new EventEmitter<PdfExportedElement>();
  filters: { [filter: string]: WidgetGroupBy[] };
  groupBys: WidgetGroupBy[];
  subGroupBys: WidgetGroupBy[];
  private $unsubscribe = new EventEmitter<void>();

  constructor(private widgetConfigService: WidgetConfigService) {
    super();
    this.filters = getWidgetFilters();
    this.groupBys = getWidgetGroupBys();
    this.subGroupBys = getWidgetSubgroupBys();
    const sub = this.ngUnsubscribe.subscribe(() => {
      this.exportEmitter.complete();
      this.$unsubscribe.emit();
      sub.unsubscribe();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.widget) {
      const redraw = difference(Object.keys(changes.widget.currentValue), [
        // omit this changes for redrawing calculation
        'title',
        'description',
      ]).find((k) => {
        if (
          !changes.widget.previousValue ||
          changes.widget.currentValue[k] !== changes.widget.previousValue[k]
        ) {
          return true;
        }
        return false;
      });
      if (redraw) {
        this.destroyChart();
        if (this.widget.type !== 'add') {
          this.createChart();
        }
      }
    }
    if (changes.export && changes.export.currentValue === true) {
      this.$isLoaded
        .pipe(
          rxFilter((val) => val),
          switchMap(() => this.getChartRenderObservable()),
          rxFilter((val) => val),
          takeUntil(this.exportEmitter)
        )
        .subscribe(() => {
          this.exportEmitter.next();
        });
    }
  }
  onChartLoad() {
    this.updateChart();
  }

  private getChartRenderObservable() {
    const chart = this.chartView?.getChart();
    return chart?.$renderCompleted ?? of(true);
  }

  onChartExport(image: string) {
    this.exported.emit({
      image,
      widget: this.widget,
      title: this.widget.title || this.altTitle,
    });
  }
  onRemoveButton() {
    this.isRemove = true;
  }
  onRemoveConfirm(answer: boolean) {
    if (answer && this.widget) {
      this.remove.emit(this.widget);
    }
    this.isRemove = false;
  }
  onExpandButton() {
    if (this.widget) {
      this.expand.emit(this.widget);
    }
  }
  onEditButton() {
    if (this.widget) {
      this.edit.emit(this.widget);
    }
  }
  createChart(widget: CustomDashboardsInfoChart = this.widget) {
    const { id, title, index, kpi, type, layout } = widget;
    this.chartId = `${id}-${title}-${kpi}_${type}-${index !== undefined ? index : 'mock'}_${Math.floor(
      Math.random() * 1000
    )}`;
    this.layout = layout;
    this.altTitle = this.generateTitle(widget);
    this.updateChart();
  }
  generateTitle(widget: CustomDashboardsInfoChart) {
    const { kpi, filter, groupBy, subGroupBy } = widget;
    let altTitle = '';
    const KPIs = this.widgetConfigService.getKPIs();
    for (const k of typeof kpi === 'string' ? [kpi] : kpi) {
      const config = KPIs[k];
      if (config) {
        altTitle += (altTitle ? ' / ' : '') + config.title;
      }
    }
    if (groupBy) {
      const _groupBy = this.groupBys.find((g) => g.id === groupBy);
      if (_groupBy) {
        altTitle += $localize` por ${_groupBy.title}:groupBy:`;
      }
    }
    if (subGroupBy) {
      const _subGroupBy = this.subGroupBys.find((g) => g.id === subGroupBy);
      if (_subGroupBy) {
        altTitle += $localize` y ${_subGroupBy.title}:subGroupBy:`;
      }
    }
    const filterTitle: string[] = [];
    if (filter) {
      for (const fname of Object.keys(filter)) {
        if (filter[fname] && this.filters[fname]) {
          const _filter = this.filters[fname].find((f: any) => f.id === filter.os);
          if (_filter) {
            filterTitle.push(_filter.title);
          }
        }
      }
    }
    return altTitle + (filterTitle.length ? ' (' + filterTitle.join(', ') + ')' : '');
  }
  updateChart() {
    this.$unsubscribe.emit();
    const { kpi, type, groupBy, subGroupBy, filter } = this.widget;
    if (!kpi || !kpi.length) {
      return this.destroyChart();
    }
    if (filter.source) {
      console.warn('Legacy `only` filter in use');
    }
    (filter as any).only = filter.source;
    if (this.chartView) {
      this.chartView.loading();
    }
    if (type === 'funnel') {
      const config = this.widgetConfigService.get({
        kpi,
        type,
        groupBy,
        subGroupBy,
        filter,
      });
      if (config) {
        this.subscribeUpdater(config);
      }
    } else {
      (typeof kpi === 'string' ? [kpi] : kpi).forEach((_kpi: string, i: number) => {
        const config = this.widgetConfigService.get({
          kpi: [_kpi],
          type,
          index: i,
          groupBy,
          subGroupBy,
          filter,
        });
        if (config) {
          this.subscribeUpdater(config);
        }
      });
    }
  }

  private subscribeUpdater(config: WidgetConfig) {
    const updater = (data: any) => {
      if (this.chartView) {
        this.chartView.update(data, this.widget.options);
        this.$isLoaded.next(Boolean(data && !data.loading));
        if (data && data.error) {
          this.isError = Boolean(data.error);
        } else {
          this.isError = false;
        }
      } else {
        console.debug('No chartView available yet');
        setTimeout(() => {
          updater(data);
        }, 10);
      }
    };
    return config.emitter().pipe(takeUntil(this.$unsubscribe)).subscribe(updater);
  }
  destroyChart() {
    this.layout = '';
    const chart = window.document.getElementById(this.chartId);
    if (chart) {
      chart.innerHTML = '';
    }
    this.chartId = '';
  }
  expandLabel(): string {
    return this.widget.fullSize ? $localize`Encoger` : $localize`Expandir`;
  }
}
